import { create } from 'zustand'
import { progressService, moduleService, labService, capstoneService } from '../services'

export const useProgressStore = create((set, get) => ({
  progress: null,
  stats: null,
  loading: false,
  error: null,

  // Fetch user's progress
  fetchProgress: async () => {
    set({ loading: true, error: null })
    try {
      // Fetch both progress and summary in parallel
      const [progressData, summaryData] = await Promise.all([
        progressService.getAll(),
        progressService.getStats()
      ])
      
      // Combine the data
      const combinedProgress = {
        ...progressData,
        total_modules: summaryData.summary?.modules?.total || 0,
        completed_modules: summaryData.summary?.modules?.completed || 0,
        total_labs: summaryData.summary?.labs?.total || 0,
        completed_labs: summaryData.summary?.labs?.completed || 0,
        total_capstones: summaryData.summary?.capstones?.total || 0,
        completed_capstones: summaryData.summary?.capstones?.completed || 0,
        total_case_studies: summaryData.summary?.case_studies?.total || 0,
        completed_case_studies: summaryData.summary?.case_studies?.completed || 0,
      }
      
      set({ progress: combinedProgress, stats: summaryData.summary, loading: false })
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch progress', loading: false })
    }
  },

  // Fetch progress stats
  fetchStats: async () => {
    try {
      const data = await progressService.getStats()
      set({ stats: data })
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  },

  // Update module progress
  updateModuleProgress: async (moduleId, progressData) => {
    try {
      await moduleService.updateProgress(moduleId, progressData)
      // Refresh progress
      await get().fetchProgress()
      return { success: true }
    } catch (error) {
      console.error('Failed to update module progress:', error)
      return { success: false, error: error.response?.data?.message }
    }
  },

  // Update lab progress
  updateLabProgress: async (labId, progressData) => {
    try {
      await labService.updateProgress(labId, progressData)
      await get().fetchProgress()
      return { success: true }
    } catch (error) {
      console.error('Failed to update lab progress:', error)
      return { success: false, error: error.response?.data?.message }
    }
  },

  // Submit lab
  submitLab: async (labId, submissionData) => {
    try {
      const data = await labService.submit(labId, submissionData)
      await get().fetchProgress()
      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.response?.data?.message }
    }
  },

  // Submit capstone
  submitCapstone: async (capstoneId, submissionData) => {
    try {
      const data = await capstoneService.submit(capstoneId, submissionData)
      await get().fetchProgress()
      return { success: true, data }
    } catch (error) {
      return { success: false, error: error.response?.data?.message }
    }
  },

  // Get completion percentage
  getCompletionPercentage: () => {
    const { progress } = get()
    if (!progress) return 0
    
    const total = (progress.total_modules || 0) + (progress.total_labs || 0) + (progress.total_capstones || 0)
    const completed = (progress.completed_modules || 0) + (progress.completed_labs || 0) + (progress.completed_capstones || 0)
    
    return total > 0 ? Math.round((completed / total) * 100) : 0
  },
}))